// =============================================================================
// JournalVouchersPage — list + create + approve + void (finance manual entries)
// =============================================================================
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, getApiError } from '../../../lib/api';
import { Card } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { IconButton } from '../../../components/IconButton';
import { Input } from '../../../components/Input';
import { Modal } from '../../../components/Modal';
import { Money } from '../../../components/Money';
import { StatusPill } from '../../../components/StatusPill';
import { PageScaffold } from '../../../components/PageScaffold';
import { DataTableCard, STICKY_HEAD } from '../../../components/DataTable';
import { cn } from '../../../lib/cn';

type JvStatus = 'DRAFT' | 'POSTED' | 'VOID';

interface JvRow {
  id: string;
  number: string;
  status: JvStatus;
  voucherDate: string;
  narration: string;
  createdBy: { id: string; name: string };
  approvedBy: { id: string; name: string } | null;
  journalEntry: { number: string; date: string } | null;
}

interface JvDraftLine {
  accountCode: string;
  debit: string;
  credit: string;
  narration: string;
}

export function JournalVouchersPage(): JSX.Element {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const list = useQuery({
    queryKey: ['journal-vouchers'],
    queryFn: () => apiGet<JvRow[]>('/journal-vouchers'),
    refetchInterval: 60_000,
  });

  const HEADER_ACTIONS = (
    <Button onClick={() => setShowForm((v) => !v)}>{showForm ? 'Cancel' : '+ New JV'}</Button>
  );

  return (
    <PageScaffold
      title="Journal Vouchers"
      subtitle="Manual GL postings — opening balances, year-end accruals, corrections. Posted entries are immutable (void writes a reversing JE)."
      actions={HEADER_ACTIONS}
      scroll={false}
    >
      {showForm && (
        <div className="shrink-0">
          <NewJvForm
            onDone={() => {
              setShowForm(false);
              qc.invalidateQueries({ queryKey: ['journal-vouchers'] });
            }}
            onCancel={() => setShowForm(false)}
          />
        </div>
      )}

      <DataTableCard>
        {list.isLoading ? (
          <div className="text-center text-ink-3 px-6 py-12">Loading…</div>
        ) : (list.data ?? []).length === 0 ? (
          <div className="text-center text-ink-3 px-6 py-12 text-sm italic">
            No journal vouchers yet.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className={STICKY_HEAD}>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                <th className="px-6 py-2 font-medium">JV</th>
                <th className="px-6 py-2 font-medium">Date</th>
                <th className="px-6 py-2 font-medium">Narration</th>
                <th className="px-6 py-2 font-medium">JE</th>
                <th className="px-6 py-2 font-medium">Created by</th>
                <th className="px-6 py-2 font-medium">Approval</th>
                <th className="px-6 py-2 font-medium">Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(list.data ?? []).map((v) => (
                <tr key={v.id} className="border-b border-rule-2 hover:bg-paper-2">
                  <td className="px-6 py-3 kc-mono text-xs text-ink">{v.number}</td>
                  <td className="px-6 py-3 text-xs text-ink-3">
                    {new Date(v.voucherDate).toLocaleDateString('en-MU', {
                      day: 'numeric',
                      month: 'short',
                    })}
                  </td>
                  <td className="px-6 py-3 text-ink max-w-md truncate">{v.narration}</td>
                  <td className="px-6 py-3 text-xs text-ink-3 kc-mono">
                    {v.journalEntry?.number ?? '—'}
                  </td>
                  <td className="px-6 py-3 text-xs text-ink-2">{v.createdBy.name}</td>
                  <td className="px-6 py-3 text-xs">
                    {v.approvedBy ? (
                      <span className="text-positive">{v.approvedBy.name}</span>
                    ) : (
                      <span className="text-warning">pending</span>
                    )}
                  </td>
                  <td className="px-6 py-3">
                    <StatusPill
                      tone={
                        v.status === 'POSTED'
                          ? 'positive'
                          : v.status === 'VOID'
                            ? 'negative'
                            : 'neutral'
                      }
                    >
                      {v.status.toLowerCase()}
                    </StatusPill>
                  </td>
                  <td className="px-6 py-3 text-right space-x-2">
                    {v.status === 'POSTED' && !v.approvedBy && <ApproveBtn jvId={v.id} />}
                    {v.status === 'POSTED' && <VoidBtn jvId={v.id} />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DataTableCard>
    </PageScaffold>
  );
}

function ApproveBtn({ jvId }: { jvId: string }): JSX.Element {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => apiPost(`/journal-vouchers/${jvId}/approve`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['journal-vouchers'] }),
  });
  return (
    <Button variant="tertiary" size="xs" onClick={() => m.mutate()} disabled={m.isPending}>
      Approve
    </Button>
  );
}

function VoidBtn({ jvId }: { jvId: string }): JSX.Element {
  const qc = useQueryClient();
  const [showVoid, setShow] = useState(false);
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () => apiPost(`/journal-vouchers/${jvId}/void`, { reason }),
    onSuccess: () => {
      setShow(false);
      qc.invalidateQueries({ queryKey: ['journal-vouchers'] });
    },
    onError: (e) => setErr(getApiError(e).message),
  });
  return (
    <>
      <Button variant="tertiary-danger" size="xs" onClick={() => setShow(true)}>
        Void
      </Button>
      {showVoid && (
        <Modal
          title="Void JV?"
          subtitle="Writes a reversing journal entry."
          onClose={() => setShow(false)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setShow(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  setErr(null);
                  m.mutate();
                }}
                disabled={!reason.trim() || m.isPending}
              >
                {m.isPending ? 'Voiding…' : 'Confirm void'}
              </Button>
            </>
          }
        >
          <Input label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
          {err && <div className="text-xs text-negative mt-2">{err}</div>}
        </Modal>
      )}
    </>
  );
}

// ─── New JV form ────────────────────────────────────────────────────────────
function NewJvForm({
  onDone,
  onCancel,
}: {
  onDone: () => void;
  onCancel: () => void;
}): JSX.Element {
  const [narration, setNarration] = useState('');
  const [voucherDate, setVoucherDate] = useState(new Date().toISOString().slice(0, 10));
  const [lines, setLines] = useState<JvDraftLine[]>([
    { accountCode: '', debit: '', credit: '', narration: '' },
    { accountCode: '', debit: '', credit: '', narration: '' },
  ]);
  const [err, setErr] = useState<string | null>(null);

  const totals = useMemo(() => {
    let d = 0,
      c = 0;
    for (const l of lines) {
      d += Number(l.debit) || 0;
      c += Number(l.credit) || 0;
    }
    return { d, c, diff: d - c };
  }, [lines]);

  const post = useMutation({
    mutationFn: () =>
      apiPost('/journal-vouchers', {
        voucherDate,
        narration,
        lines: lines
          .filter((l) => l.accountCode.trim() && (Number(l.debit) > 0 || Number(l.credit) > 0))
          .map((l) => ({
            accountCode: l.accountCode.trim(),
            debit: Number(l.debit) || undefined,
            credit: Number(l.credit) || undefined,
            narration: l.narration || undefined,
          })),
      }),
    onSuccess: onDone,
    onError: (e) => setErr(getApiError(e).message),
  });

  const canPost =
    !!narration.trim() && totals.d > 0 && Math.abs(totals.diff) < 0.005 && !post.isPending;

  return (
    <Card
      title="New journal voucher"
      subtitle="Debits must equal credits. Account codes (e.g. 1101, 4100) are typed in — see /accounting/trial-balance for the full chart."
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Input
            label="Voucher date"
            type="date"
            value={voucherDate}
            onChange={(e) => setVoucherDate(e.target.value)}
          />
          <div className="sm:col-span-2">
            <Input
              label="Narration"
              value={narration}
              onChange={(e) => setNarration(e.target.value)}
              placeholder="e.g. Opening balance bump for Cash – HQ"
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">Lines</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                setLines((cur) => [
                  ...cur,
                  { accountCode: '', debit: '', credit: '', narration: '' },
                ])
              }
            >
              + Add line
            </Button>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-ink-3">
                <th className="px-2 py-1 font-medium w-28">Account</th>
                <th className="px-2 py-1 font-medium">Narration</th>
                <th className="px-2 py-1 font-medium text-right w-28">Debit</th>
                <th className="px-2 py-1 font-medium text-right w-28">Credit</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, idx) => (
                <tr key={idx}>
                  <td className="px-2 py-1">
                    <input
                      type="text"
                      value={l.accountCode}
                      onChange={(e) =>
                        setLines((cur) =>
                          cur.map((x, i) =>
                            i === idx ? { ...x, accountCode: e.target.value } : x,
                          ),
                        )
                      }
                      placeholder="1101"
                      className="kc-input text-sm kc-mono"
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      type="text"
                      value={l.narration}
                      onChange={(e) =>
                        setLines((cur) =>
                          cur.map((x, i) => (i === idx ? { ...x, narration: e.target.value } : x)),
                        )
                      }
                      placeholder="line note (optional)"
                      className="kc-input text-sm"
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={l.debit}
                      onChange={(e) =>
                        setLines((cur) =>
                          cur.map((x, i) =>
                            i === idx
                              ? {
                                  ...x,
                                  debit: e.target.value,
                                  credit: e.target.value ? '' : x.credit,
                                }
                              : x,
                          ),
                        )
                      }
                      className="kc-input text-sm text-right"
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={l.credit}
                      onChange={(e) =>
                        setLines((cur) =>
                          cur.map((x, i) =>
                            i === idx
                              ? {
                                  ...x,
                                  credit: e.target.value,
                                  debit: e.target.value ? '' : x.debit,
                                }
                              : x,
                          ),
                        )
                      }
                      className="kc-input text-sm text-right"
                    />
                  </td>
                  <td className="px-2 py-1">
                    {lines.length > 2 && (
                      <IconButton
                        aria-label="Remove line"
                        variant="danger"
                        size="sm"
                        onClick={() => setLines((cur) => cur.filter((_, i) => i !== idx))}
                      >
                        ×
                      </IconButton>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="border-t border-rule pt-3 flex items-center justify-between">
          <div className="text-sm">
            Σ Debits:{' '}
            <span className="kc-mono text-ink">
              <Money value={totals.d} />
            </span>
            {' · '}Σ Credits:{' '}
            <span className="kc-mono text-ink">
              <Money value={totals.c} />
            </span>
            {' · '}
            <span
              className={cn(
                'kc-mono font-medium',
                Math.abs(totals.diff) < 0.005 ? 'text-positive' : 'text-negative',
              )}
            >
              {Math.abs(totals.diff) < 0.005 ? 'Balanced' : `Diff ${totals.diff.toFixed(2)}`}
            </span>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setErr(null);
                post.mutate();
              }}
              disabled={!canPost}
            >
              {post.isPending ? 'Posting…' : 'Post journal'}
            </Button>
          </div>
        </div>
        {err && <div className="text-xs text-negative">{err}</div>}
      </div>
    </Card>
  );
}
